import { useState, useEffect, useRef } from 'react';

const ZNS_API_BASE = 'https://zns.bio/api';
const INK_CHAIN_ID = 57073;

interface ZNSResponse {
  code: number;
  primaryDomain?: string;
  userOwnedDomains?: string[];
}

// Cache for ZNS lookups (address -> domain or null)
const znsCache: Map<string, { domain: string | null; timestamp: number }> = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache
const FAILED_LOOKUP_CACHE_DURATION = 60 * 1000; // 1 minute cache for failed lookups

// Track ongoing fetches to prevent duplicate concurrent calls
const ongoingFetches: Map<string, Promise<string | null>> = new Map();

/**
 * Check if an address is an Ethereum address (starts with 0x and is 42 chars)
 * Solana addresses are base58 encoded and won't start with 0x
 */
function isEthereumAddress(address: string): boolean {
  return address.startsWith('0x') && address.length === 42;
}

/**
 * Hook to fetch ZNS domain name for a wallet address on Ink
 * @param address - Wallet address to lookup
 * @param enabled - Whether to enable ZNS lookup (default: true). Set to false for non-Ink chains
 * @returns primaryDomain (e.g., "deployerone.ink") or null if not found
 */
export function useZNSName(address: string | undefined, enabled: boolean = true): {
  znsName: string | null;
  isLoading: boolean;
  error: string | null;
} {
  const [znsName, setZnsName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    if (!address || !enabled) {
      setZnsName(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Skip if not an Ethereum address (e.g., Solana addresses)
    if (!isEthereumAddress(address)) {
      setZnsName(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Check cache first
    const normalizedAddress = address.toLowerCase();
    const cached = znsCache.get(normalizedAddress);
    if (cached) {
      const cacheAge = Date.now() - cached.timestamp;
      const cacheDuration = cached.domain ? CACHE_DURATION : FAILED_LOOKUP_CACHE_DURATION;
      
      if (cacheAge < cacheDuration) {
        setZnsName(cached.domain);
        setIsLoading(false);
        setError(null);
        return;
      }
    }

    // Prevent concurrent fetches for the same address
    if (isFetchingRef.current) {
      return;
    }

    const fetchZNSName = async () => {
      // Check if there's already an ongoing fetch for this address
      const existingFetch = ongoingFetches.get(normalizedAddress);
      if (existingFetch) {
        try {
          const domain = await existingFetch;
          setZnsName(domain);
          setIsLoading(false);
          return;
        } catch {
          // If the existing fetch fails, continue with new fetch
        }
      }

      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);

      const fetchPromise = (async () => {
        try {
          const url = `${ZNS_API_BASE}/resolveAddress?chain=${INK_CHAIN_ID}&address=${normalizedAddress}`;
          
          const response = await fetch(url, {
            // Add timeout to prevent hanging requests
            signal: AbortSignal.timeout(10000) // 10 second timeout
          });
          
          if (!response.ok) {
            // Don't throw for 500 errors - just cache the failure
            if (response.status >= 500) {
              console.warn(`[ZNS] Server error ${response.status} for address ${normalizedAddress}, caching failure`);
              znsCache.set(normalizedAddress, { domain: null, timestamp: Date.now() });
              return null;
            }
            throw new Error(`ZNS API error: ${response.status}`);
          }

          const data: ZNSResponse = await response.json();
          
          if (data.code === 200 && data.primaryDomain) {
            // Add .ink suffix if not already present
            const domain = data.primaryDomain.endsWith('.ink') 
              ? data.primaryDomain 
              : `${data.primaryDomain}.ink`;
            
            // Cache successful lookup
            znsCache.set(normalizedAddress, { domain, timestamp: Date.now() });
            return domain;
          } else {
            // Cache failed lookup (no domain found)
            znsCache.set(normalizedAddress, { domain: null, timestamp: Date.now() });
            return null;
          }
        } catch (err: any) {
          // Handle timeout or network errors
          if (err.name === 'AbortError' || err.name === 'TimeoutError') {
            console.warn(`[ZNS] Request timeout for address ${normalizedAddress}`);
          } else if (err.message?.includes('500')) {
            console.warn(`[ZNS] Server error for address ${normalizedAddress}`);
          } else {
            console.error(`[ZNS] Error fetching domain for ${normalizedAddress}:`, err);
          }
          
          // Cache failed lookup for a shorter duration
          znsCache.set(normalizedAddress, { domain: null, timestamp: Date.now() });
          setError(err instanceof Error ? err.message : 'Unknown error');
          return null;
        } finally {
          ongoingFetches.delete(normalizedAddress);
          isFetchingRef.current = false;
        }
      })();

      ongoingFetches.set(normalizedAddress, fetchPromise);
      
      const domain = await fetchPromise;
      setZnsName(domain);
      setIsLoading(false);
    };

    fetchZNSName();
  }, [address, enabled]);

  return { znsName, isLoading, error };
}

